import React from "react";
import { Link, useParams, useLocation } from "react-router-dom";
import { ArrowUpRight, Clock, Tag, CheckCircle2 } from "lucide-react";
import SEO from "@/components/SEO";
import Breadcrumbs from "@/components/Breadcrumbs";
import { Reveal, Stagger, Item } from "@/components/Reveal";
import { useFetch } from "@/lib/useData";
import RichHtml from "@/components/RichHtml";

export default function ServiceDetail() {
  const { slug } = useParams();
  const { pathname } = useLocation();
  const { data } = useFetch(`/services/${slug}`, [slug]);
  const { data: related } = useFetch(data ? `/services?division=${data.division_slug}` : "/services", [data?.division_slug]);

  if (!data) return <div className="min-h-screen pt-32" />;
  const sibling = (related || []).filter((s) => s.slug !== slug).slice(0, 3);

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "MedicalProcedure",
    name: data.title,
    description: data.description,
    url: `https://infinitymg.so${pathname}`,
    provider: { "@type": "MedicalOrganization", name: "Infinity Medical Group" },
  };

  return (
    <>
      <SEO title={`${data.title} | Infinity Medical Group`} description={data.summary} path={pathname} jsonLd={jsonLd} />
      <Breadcrumbs overrideLast={data.title} />

      <section className="bg-white pt-4 pb-16" data-testid="service-hero">
        <div className="container max-w-4xl">
          <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] font-semibold text-teal">
            <Tag className="h-3.5 w-3.5" /> {data.division_slug?.replace("-", " ")}
          </span>
          <h1 className="mt-5 font-display font-bold text-4xl md:text-6xl text-navy-800 tracking-tight">{data.title}</h1>
          {data.summary && <p className="mt-6 text-lg leading-relaxed text-navy-700/80">{data.summary}</p>}
          <RichHtml html={data.description} className="mt-6 text-base leading-relaxed" testId="service-description" />
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to={`/contact?service=${slug}`} data-testid="service-cta-quote" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-navy text-white text-sm font-semibold hover:bg-teal transition-colors">
              Request a quote <ArrowUpRight className="h-4 w-4" />
            </Link>
            <Link to={`/contact?service=${slug}&subject=Appointment+request`} data-testid="service-cta-book" className="inline-flex items-center gap-2 px-6 py-3 rounded-full ring-1 ring-navy/20 text-navy text-sm font-semibold hover:bg-navy hover:text-white transition-colors">
              Book a consultation
            </Link>
          </div>
        </div>
      </section>

      <section className="bg-soft-gray py-16 lg:py-20">
        <div className="container grid gap-12 lg:grid-cols-12 max-w-6xl">
          <div className="lg:col-span-7">
            {data.includes?.length > 0 && (
              <>
                <h2 className="font-display font-semibold text-2xl md:text-3xl text-navy-800">What's included</h2>
                <Stagger className="mt-6 grid gap-3">
                  {data.includes.map((i) => (
                    <Item key={i}>
                      <div className="flex items-start gap-3 p-4 rounded-xl bg-white border border-navy-50">
                        <CheckCircle2 className="h-5 w-5 text-teal shrink-0 mt-0.5" />
                        <p className="text-base text-navy-800">{i}</p>
                      </div>
                    </Item>
                  ))}
                </Stagger>
              </>
            )}
          </div>
          <aside className="lg:col-span-5">
            <div className="p-7 rounded-2xl bg-navy text-white">
              <h3 className="font-display font-semibold text-lg">Service summary</h3>
              <dl className="mt-5 space-y-4 text-sm">
                {data.duration && (
                  <div>
                    <dt className="text-white/55 uppercase tracking-[0.18em] text-[11px] font-semibold">Duration</dt>
                    <dd className="mt-1 text-white/90 inline-flex items-center gap-1.5"><Clock className="h-4 w-4 text-teal-light" /> {data.duration}</dd>
                  </div>
                )}
                {data.price_note && (
                  <div>
                    <dt className="text-white/55 uppercase tracking-[0.18em] text-[11px] font-semibold">Pricing</dt>
                    <dd className="mt-1 text-white/90">{data.price_note}</dd>
                  </div>
                )}
              </dl>
              <Link to={`/contact?service=${slug}`} className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-teal text-white text-sm font-semibold hover:bg-teal-light hover:text-navy transition-colors">
                Enquire now <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>
          </aside>
        </div>
      </section>

      {sibling.length > 0 && (
        <section className="bg-white py-16 lg:py-20">
          <div className="container">
            <h2 className="font-display font-semibold text-2xl md:text-3xl text-navy-800">Related services</h2>
            <div className="mt-8 grid gap-5 md:grid-cols-3">
              {sibling.map((s) => (
                <Link key={s.slug} to={`/services/${s.slug}`} className="block p-6 rounded-2xl bg-soft-gray border border-navy-50 hover:bg-white hover:shadow-soft transition-all">
                  <h3 className="font-display font-semibold text-lg text-navy-800">{s.title}</h3>
                  <p className="mt-2 text-sm text-navy-700/75 line-clamp-2">{s.summary}</p>
                  <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-teal">View <ArrowUpRight className="h-4 w-4" /></span>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </>
  );
}
